import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import ErrorScreen from './common/ErrorScreen';
import { DisplayWidget } from './components/DisplayWidget';
import { GuidPage } from './pages/GuidPage';

function App({ data }) {
  const [htmlContent, setHtmlContent] = useState('');
  const containerRef = useRef(null);

  // -----------------------------
  // 1. Memoize URL params
  // -----------------------------
  const { guid, token, bguid, siteUrl } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      guid: params.get('guid'),
      token: params.get('token'),
      bguid: params.get('bguid'),
      siteUrl: params.get('website'),
    };
  }, []);

  // -----------------------------
  // 2. Memoize defaultData
  // -----------------------------
  const defaultData = useMemo(
    () => ({
      guid: guid || data.guid,
      pcat: data?.pcat || data?.dynamicCategory,
      pid: data?.pid,
      bguid: bguid || data?.bguid,
      version: data?.version,
      fromRedis: data?.fromRedis,
      fromApp: data?.fromApp,
      autoTrigger: data?.autoTrigger,
      reTarget: data?.reTarget,
    }),
    [guid, bguid, data],
  ); // ← Prettier wanted a trailing comma here

  // -----------------------------
  // 3. Set favicon once
  // -----------------------------
  useEffect(() => {
    const host = window.location.host.toLowerCase();
    const faviconUrl = host.includes('feefo') ? 'https://social.feefo.com/FeefoMiniLogo.png' : '/Idukki-Short.png'; // ← Prettier wanted this on one line

    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, []);

  // -----------------------------
  // 4. Memoized DOM lookup
  // -----------------------------
  const element = useMemo(() => {
    const sources = ['idukki', 'feefo'];
    return sources.map((source) => document.querySelector(`[data-ugc='${source}']`)).find(Boolean); // ← Prettier wanted this flattened
  }, []);

  // -----------------------------
  // 5. Helper: check if parent hidden
  // -----------------------------
  const isParentDisplayNone = useCallback((el) => {
    while (el) {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return true;
      el = el.parentElement;
    }
    return false;
  }, []);

  // -----------------------------
  // 6. Helper: check if widget already mounted
  // -----------------------------
  const isAlreadyPresent = useCallback(() => {
    const parent = document.querySelector(`[data-guid="${defaultData.guid}"]`);
    if (!parent) return false;
    return parent.querySelectorAll('.Idukki-App').length > 0;
  }, [defaultData.guid]);

  // -----------------------------
  // 7. Fetch external HTML (if siteUrl provided)
  // -----------------------------
  const fetchAndInjectHTML = useCallback(async () => {
    try {
      const response = await fetch(`https://app.idukki.io/api/fetch-website?url=${siteUrl}`);
      const html = await response.json();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html.data, 'text/html');
      setHtmlContent(doc.documentElement.innerHTML);
    } catch (err) {
      console.error('Failed to fetch HTML:', err);
    }
  }, [siteUrl]);

  useEffect(() => {
    if (siteUrl) fetchAndInjectHTML();
  }, [siteUrl, fetchAndInjectHTML]);

  // -----------------------------
  // 8. Inject widget into fetched HTML
  // -----------------------------
  useEffect(() => {
    if (!htmlContent || !containerRef.current) return;

    const footer = containerRef.current.querySelector('footer');
    if (!footer) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'Idukki-App';
    footer.parentNode.insertBefore(wrapper, footer);

    import('react-dom/client').then(({ createRoot }) => {
      createRoot(wrapper).render(<DisplayWidget props={defaultData} />);
    });
  }, [htmlContent, defaultData]);

  // -----------------------------
  // 9. GUID validation
  // -----------------------------
  const isGuidValid = useCallback((g) => {
    if (!g || g === '') return false;
    if (g.startsWith('{{') && g.endsWith('}}')) return false;
    return true;
  }, []);

  // -----------------------------
  // 10. Rendering logic
  // -----------------------------
  if (isGuidValid(defaultData.guid)) {
    if (!isParentDisplayNone(element)) {
      if (!isAlreadyPresent()) {
        if (siteUrl) {
          return (
            <main role="main" className="idk-widget-main">
              <idk-div ref={containerRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </main>
          );
        }
        return (
          <main role="main" className="idk-widget-main">
            <idk-div class="Idukki-App">
              <DisplayWidget props={defaultData} />
            </idk-div>
          </main>
        );
      }
    }
  }

  if (token) {
    return (
      <main role="main" className="idk-widget-main">
        <idk-div class="Idukki-App">
          <GuidPage bguid={defaultData.bguid} token={token} />
        </idk-div>
      </main>
    );
  }

  return (
    <main role="main" className="idk-widget-main">
      <ErrorScreen error="Error: Token is invalid" />
    </main>
  );
}

export default App;
